/**
 * v-tooltip directive
 *
 * Usage:
 *   v-tooltip="'Some help text'"
 *   v-tooltip.top="'Appears above'"       (default: bottom)
 *   v-tooltip.left="'Appears left'"
 *
 * Renders a small dark tooltip on hover. Auto-repositions to stay on screen.
 */

const TOOLTIP_CLASS = 'v-tooltip-el'
const ARROW_SIZE = 5
const GAP = 6

function getPlacement(modifiers) {
  if (modifiers.top) return 'top'
  if (modifiers.left) return 'left'
  if (modifiers.right) return 'right'
  return 'bottom'
}

function createTooltipEl() {
  const el = document.createElement('div')
  el.className = TOOLTIP_CLASS
  Object.assign(el.style, {
    position: 'fixed',
    zIndex: '99999',
    background: '#1f2937',
    color: '#fff',
    fontSize: '11px',
    lineHeight: '1.4',
    padding: '4px 8px',
    borderRadius: '4px',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    opacity: '0',
    transition: 'opacity 0.15s',
    maxWidth: '280px'
  })
  return el
}

function positionTooltip(tooltip, rect, placement) {
  const tw = tooltip.offsetWidth
  const th = tooltip.offsetHeight
  const pad = 4
  let top, left

  switch (placement) {
    case 'top':
      top = rect.top - th - GAP
      left = rect.left + rect.width / 2 - tw / 2
      break
    case 'left':
      top = rect.top + rect.height / 2 - th / 2
      left = rect.left - tw - GAP
      break
    case 'right':
      top = rect.top + rect.height / 2 - th / 2
      left = rect.right + GAP
      break
    default: // bottom
      top = rect.bottom + GAP
      left = rect.left + rect.width / 2 - tw / 2
  }

  // Clamp to viewport
  if (left + tw > window.innerWidth - pad) left = window.innerWidth - tw - pad
  if (left < pad) left = pad
  if (top + th > window.innerHeight - pad) top = rect.top - th - GAP
  if (top < pad) top = rect.bottom + GAP

  tooltip.style.top = `${top}px`
  tooltip.style.left = `${left}px`
}

function show(el) {
  const text = el._tooltipText
  if (!text) return

  let tooltip = el._tooltipEl
  if (!tooltip) {
    tooltip = createTooltipEl()
    el._tooltipEl = tooltip
  }
  tooltip.textContent = text
  document.body.appendChild(tooltip)

  // Position after the element is in the DOM so we can measure
  requestAnimationFrame(() => {
    const rect = el.getBoundingClientRect()
    positionTooltip(tooltip, rect, el._tooltipPlacement)
    tooltip.style.opacity = '1'
  })
}

function hide(el) {
  const tooltip = el._tooltipEl
  if (tooltip && tooltip.parentNode) {
    tooltip.style.opacity = '0'
    setTimeout(() => {
      if (tooltip.parentNode) tooltip.parentNode.removeChild(tooltip)
    }, 150)
  }
}

export const vTooltip = {
  mounted(el, binding) {
    el._tooltipText = binding.value || ''
    el._tooltipPlacement = getPlacement(binding.modifiers)
    el._tooltipShow = () => show(el)
    el._tooltipHide = () => hide(el)
    el.addEventListener('mouseenter', el._tooltipShow)
    el.addEventListener('mouseleave', el._tooltipHide)
    el.addEventListener('click', el._tooltipHide)
  },
  updated(el, binding) {
    el._tooltipText = binding.value || ''
    el._tooltipPlacement = getPlacement(binding.modifiers)
  },
  beforeUnmount(el) {
    hide(el)
    el.removeEventListener('mouseenter', el._tooltipShow)
    el.removeEventListener('mouseleave', el._tooltipHide)
    el.removeEventListener('click', el._tooltipHide)
    delete el._tooltipEl
    delete el._tooltipText
    delete el._tooltipPlacement
  }
}
